import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/form';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { useConnectClaudeCode } from '../hooks/use-connect-claude-code';
import { CLAUDE_KEY_MESSAGES } from '../model/claude-key';
import {
  ENVIRONMENT_KEY_DEFAULTS,
  type EnvironmentKeyValues,
  environmentKeySchema,
} from '../schemas/environment-key-schema';

type ClaudeKeyFormProps = {
  mode: 'connect' | 'replace';
  onSaved: () => void;
  onCancel: () => void;
};

/** Connect or replace the key on the Connections page (design: Setup). */
export function ClaudeKeyForm({ mode, onSaved, onCancel }: ClaudeKeyFormProps) {
  const form = useForm<EnvironmentKeyValues>({
    resolver: zodResolver(environmentKeySchema),
    defaultValues: ENVIRONMENT_KEY_DEFAULTS,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const { handleSubmit, register, setError, clearErrors, formState } = form;
  const { connectClaudeCode } = useConnectClaudeCode();

  const onSubmit = async ({ environmentKey }: EnvironmentKeyValues) => {
    const outcome = await connectClaudeCode(environmentKey);
    if (outcome.isConnected) {
      onSaved();
      return;
    }
    setError('environmentKey', { type: 'server', message: CLAUDE_KEY_MESSAGES[outcome.problem] });
  };

  const idleMessage = mode === 'connect' ? CLAUDE_KEY_MESSAGES.idle : CLAUDE_KEY_MESSAGES.replace;
  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-2">
        <FormField name="environmentKey">
          <FormItem className="gap-2">
            <FormLabel>{mode === 'connect' ? 'Environment key' : 'New environment key'}</FormLabel>
            <div className="flex flex-wrap gap-2">
              <FormControl hasDescription>
                <Input
                  isMono
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="cse_live_…"
                  className="w-auto shrink grow basis-75"
                  {...register('environmentKey', { onChange: () => clearErrors('environmentKey') })}
                />
              </FormControl>
              <Button
                type="submit"
                variant="primary"
                size="field"
                isLoading={formState.isSubmitting}
              >
                Save key
              </Button>
              <Button variant="ghost" size="field" onClick={onCancel}>
                Cancel
              </Button>
            </div>
            <FormMessage description={idleMessage} />
          </FormItem>
        </FormField>
      </form>
    </FormProvider>
  );
}
